/**
 * vatIncome — geteilte Normalisierung `invoices`-Doc → {@link VatIncomeEntry}
 * (M-CFO-0, Extraktion aus `pages/UmsatzsteuerPage.tsx:121-163`).
 *
 * WARUM diese Datei existiert (Copy-Paste-Verbot, Golden Rule „Shared Mapper"):
 * die USt-Rücklage-Kachel (M-CFO-2) und die UStVA-Vorschau-Seite (M8b) MÜSSEN
 * dieselbe Einnahmen-Normalform verwenden — jede Divergenz hier wäre eine
 * steuerliche Inkonsistenz zwischen Cockpit-Kachel und UStVA-Seite. Der Mapper
 * ist **byte-identisch** aus der Seite übernommen (kein Verhaltensdelta); die
 * Seite importiert ihn künftig von hier (Parity-Gate: bestehende
 * `UmsatzsteuerPage`-Tests bleiben ohne Assertion-Änderung grün).
 *
 * Reine Funktion: kein Firestore-, kein React-Import. Der Firestore-Read lebt in
 * den aufrufenden Loadern (`loaders/cockpitLoaders#loadVatIncomeForUser`,
 * `UmsatzsteuerPage` via `datevSource#loadInvoicesForUser`).
 *
 * @module vatIncome
 */

import { Timestamp } from 'firebase/firestore';

import { InvoiceData } from '../types/invoiceTypes';
import { InvoiceTaxCategoryKey, Bt151CategoryCode } from './taxCategoryCatalog';
import { reconstructPerRate } from './invoicePerRate';
import { VatIncomeEntry } from './vatReturn';

// ─── Firestore invoice doc shape (nur der vom Mapper benötigte Subset) ───────────

/**
 * Minimal-Subset eines `invoices`-Firestore-Dokuments, das
 * {@link mapInvoiceToVatIncomeEntry} konsumiert. Superset-Doc-Formen (z. B. die
 * UStVA-Seite `InvoiceDoc` oder der Cockpit-Loader) sind strukturell zuweisbar.
 */
export interface VatInvoiceDoc {
  id: string;
  invoiceDate?: string | Timestamp;
  isCancellation?: boolean;
  isCancelled?: boolean;
  /** Komplettes InvoiceData (einzige Quelle für Per-Satz-Split + BT-72). */
  rawData?: Partial<InvoiceData>;
}

// ─── Date helpers (bewusst lokal dupliziert, Muster „Duplikat-Lader akzeptiert") ──

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Extrahiert YYYY-MM-DD aus Timestamp/String/undefined. */
function toDateISO(value: string | Timestamp | null | undefined): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Timestamp) {
    const d = value.toDate();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }
  return null;
}

/** Belegdatum-Priorität: top-level `invoiceDate` → `rawData.invoiceDate` → heute. */
function extractInvoiceDate(inv: VatInvoiceDoc): string {
  const topLevel = toDateISO(inv.invoiceDate);
  if (topLevel) return topLevel;
  if (inv.rawData?.invoiceDate) return inv.rawData.invoiceDate.slice(0, 10);
  return getTodayISO();
}

// ─── Income mapper: VatInvoiceDoc → VatIncomeEntry (entkoppelte Normalform) ───────

/**
 * Mappt ein Firestore-`invoices`-Doc auf die entkoppelte {@link VatIncomeEntry}-Form.
 *
 * Periodenkriterium (Soll): BT-72 Leistungsdatum (`rawData.deliveryDate`). Fehlt es →
 * `serviceDateISO = null` + `periodDateIsFallback = true` (buildVatReturn fällt dann auf
 * das Rechnungsdatum zurück, die UI markiert das als unsicher).
 *
 * `isDownPayment = false` (Rev. 3, Writer-Beweis §7.5): Anzahlungs-/Abschlags- und
 * Schlussrechnungen liegen tatsächlich in der `invoices`-Collection (top-level
 * `isDownPayment`/`type`/`notes` + `rawData`, `InvoiceRepository.php:160-164`), NICHT in
 * einer separaten `downPayments`-Collection (die es nicht gibt). Sie werden hier bewusst
 * als normale Soll-Einnahme zum Rechnungsdatum gewertet — die anzahlungsspezifische
 * Mindest-Ist-Logik (§13(1)1a S.4) braucht `paidInPeriodGrossCents` und bleibt M8c-Scope.
 * Der UStVA-XML-Export ist gegen diese Untererfassung über Gate B9 geschützt
 * (`detectDownPaymentPresence`, §7.5) — reine Doku-Korrektur, kein Verhaltensdelta.
 *
 * Storno-Handling (§3.7 rev.3): rateLines × −1, damit `buildVatReturn` storno-neutral
 * summieren kann. KEIN Herausfiltern der Storno-Docs — der Netting-Effekt entsteht aus
 * den Vorzeichen (deshalb reicht `loadVatIncomeForUser` alle Docs ungefiltert durch).
 *
 * @param inv Firestore-`invoices`-Dokument (Subset).
 * @returns Normalisierte Einnahme für `buildVatReturn`.
 */
export function mapInvoiceToVatIncomeEntry(inv: VatInvoiceDoc): VatIncomeEntry {
  // Storno-Erkennung vorab (top-level flag — analog DatevExportPage:51-52).
  const isCancellationDoc = inv.isCancellation === true || inv.rawData?.isCancellation === true;

  // Storno-Reversal (§3.7 rev.3): rateLines × −1, damit buildVatReturn storno-neutral summieren
  // kann. Das Storno reverst seinen Umsatz in SEINER Periode — die Originalrechnung (`isCancelled`)
  // bleibt positiv. KEINE Steuer-Klassifikation hier — reine Integer-Multiplikation.
  const sign = isCancellationDoc ? -1 : 1;

  const perRate = reconstructPerRate(inv.rawData);
  const rateLines = perRate.map((r) => ({
    ratePercent: r.taxRate,
    netCents: r.netCents * sign,   // bei Storno-Reversal negativ (vorzeichen-aware, §3.7)
    vatCents: r.vatCents * sign,   // bei Storno-Reversal negativ
    // Persistierte VB-1-Auswahl (verlustfrei) hat Vorrang; null bei Altbestand.
    selection: (r.taxCategorySelection as InvoiceTaxCategoryKey | null) ?? null,
    categoryCode: (r.taxCategory as Bt151CategoryCode | null) ?? null,
  }));
  // grossCents ergibt sich als Summe der (ggf. negierten) Zeilen — bei Storno-Reversal negativ.
  const grossCents = rateLines.reduce((sum, l) => sum + l.netCents + l.vatCents, 0);

  // BT-72 Leistungsdatum (§3.1): bevorzugtes Soll-Periodenkriterium; fehlt → Fallback.
  const serviceDateISO = inv.rawData?.deliveryDate
    ? inv.rawData.deliveryDate.slice(0, 10)
    : null;

  // BT-48 Käufer-USt-IdNr. — Gate für IGL → KZ 41 (sonst Bucket). Defensiv aus rawData.buyer.
  const buyerVatId = inv.rawData?.buyer?.vatNumber;
  const hasBuyerVatId = typeof buyerVatId === 'string' && buyerVatId.trim().length > 0;

  return {
    id: inv.id,
    serviceDateISO,
    invoiceDateISO: extractInvoiceDate(inv),
    periodDateIsFallback: serviceDateISO === null,
    rateLines,
    grossCents,
    isDownPayment: false, // siehe JSDoc — Anzahlungen liegen in `invoices`, Ist-Anteil = M8c
    hasBuyerVatId,
    isCancellation: isCancellationDoc,
    isCancellationReversal: isCancellationDoc, // diagnostisch; Netting via Vorzeichen (§3.7)
  };
}
